import { addMinutes } from "date-fns"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import InfoGrid, {
  type InfoGridProps,
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import InvitationLogStatus from "@/v2-backoffice-client/invitation/log/InvitationLogStatus"
import type { InvitationFragment } from "@/v2-backoffice-client/invitation/__types__/invitation.fragment"

const FIELDS = createInfoGridFields<InvitationFragment>({
  fields: {
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
    Email: {
      label: "Email",
      accessor: "Email",
      valueClassName: "truncate",
    },
    Name: {
      label: "Name",
      accessor: (data) => formatUserName(data.FirstName, data.LastName),
    },
    Tenant: {
      label: "Team",
      accessor: (data) => data.Tenant?.Name,
      to: ({ data }) => `>>?v=tenant:${data.TenantId}`,
    },
    Role: {
      label: "Role",
      accessor: (data) => data.UserTenantType?.Name,
    },
    Project: {
      label: "Project",
      accessor: (data) => data.Project?.Name,
    },
    Language: {
      label: "Language",
      accessor: "Language",
    },
    Status: {
      label: "Status",
      accessor: (data) => data.LatestLog?.StatusName,
      renderValue: ({ data }) => {
        if (!data.LatestLog) return null
        return <InvitationLogStatus log={data.LatestLog} size="sm" />
      },
    },
    ConsumedByUser: {
      label: "User",
      accessor: (data) =>
        data.ConsumedByUser
          ? formatUserName(
              data.ConsumedByUser.FirstName,
              data.ConsumedByUser.LastName,
            )
          : undefined,
      show: (data) => Boolean(data.ConsumedByUser),
      to: ({ data }) =>
        data.ConsumedByUser ? `>>?v=user:${data.ConsumedByUser.Id}` : undefined,
    },
    ExpiresAt: {
      label: "Expires",
      accessor: (data) => {
        const expiresAt = addMinutes(new Date(data.CreatedAt), data.MinuteLimit)
        return expiresAt.toISOString()
      },
      renderValue: ({ data }) => {
        const expiresAt = addMinutes(new Date(data.CreatedAt), data.MinuteLimit)
        const isExpired = expiresAt.getTime() <= Date.now()
        return (
          <DotText variant={isExpired ? "destructive" : "success-1"}>
            {formatDateString(expiresAt)}
          </DotText>
        )
      },
    },
    Referral: {
      label: "Referral",
      accessor: "Referral",
      defaultHidden: true,
      valueClassName: "truncate font-mono text-xs",
    },
    CreatedBy: {
      label: "Created By",
      accessor: (data) =>
        formatUserName(
          data.CreatedByUser?.FirstName,
          data.CreatedByUser?.LastName,
        ),
    },
    CreatedAt: {
      label: "Created",
      accessor: "CreatedAt",
      renderValue: ({ value }) => {
        if (!value) return null
        return formatDateString(String(value))
      },
    },
  },
})

export type InvitationViewInfoProps = Omit<
  InfoGridProps<InvitationFragment>,
  "data" | "fields"
> & {
  invitation: InvitationFragment | null | undefined
}

function InvitationViewInfo(props: InvitationViewInfoProps) {
  const { invitation, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="invitation-info"
      className={cn("invitation-view-info", className)}
      data={invitation}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default InvitationViewInfo
